import React, { useMemo } from 'react';
import { CheckPicker, DateRangePicker, Input, SelectPicker } from 'rsuite';
import { FilterField, FilterToolbar } from '@nsi-advisory-services/components';
import { normalizeExpenseStatus } from '../../utils/expenseStatus';
import { CATEGORY_OPTIONS } from '../../utils/expenseCategories';

export const EMPTY_EXPENSE_FILTERS = {
  search: '',
  status: null,
  dates: null,
  categories: [],
  paid: null,
  customer: null,
};

const STATUS_OPTIONS = [
  { label: 'Draft', value: 'draft' },
  { label: 'Pending', value: 'pending' },
  { label: 'Rejected', value: 'rejected' },
  { label: 'Approved', value: 'approved' },
];

const PAID_OPTIONS = [
  { label: 'Paid', value: 'paid' },
  { label: 'Not paid yet', value: 'unpaid' },
];

const isoDay = (date) => [
  date.getFullYear(),
  String(date.getMonth() + 1).padStart(2, '0'),
  String(date.getDate()).padStart(2, '0'),
].join('-');

const CATEGORY_LABELS = Object.fromEntries(CATEGORY_OPTIONS.map((option) => [option.value, option.label]));

// Stored categories are slugs (office_supplies); anything not in the list still reads as words.
const categoryLabel = (value) => CATEGORY_LABELS[value]
  || value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());

const distinctOptions = (expenses, key, toLabel = (value) => value) => [...new Set(
  expenses.map((expense) => String(expense?.[key] || '').trim()).filter(Boolean)
)]
  .map((value) => ({ label: toLabel(value), value }))
  .sort((a, b) => a.label.localeCompare(b.label));

export function filterExpenses(expenses, filters) {
  const search = filters.search.trim().toLowerCase();
  const [from, to] = filters.dates ? filters.dates.map(isoDay) : [];

  return expenses.filter((expense) => {
    if (filters.status && normalizeExpenseStatus(expense.status) !== filters.status) return false;
    if (filters.categories.length && !filters.categories.includes(String(expense.category || '').trim())) return false;
    if (filters.customer && String(expense.customer_name || '').trim() !== filters.customer) return false;
    if (filters.paid && Boolean(expense.paid_on) !== (filters.paid === 'paid')) return false;
    if (from) {
      const day = String(expense.purchase_date || '').slice(0, 10);
      if (!day || day < from || day > to) return false;
    }
    if (search) {
      const text = [expense.description, expense.vendor_name, expense.customer_name, expense.category, expense.group_name]
        .join(' ')
        .toLowerCase();
      if (!text.includes(search)) return false;
    }
    return true;
  });
}

export default function ExpenseFilters({ expenses, value, onChange }) {
  const update = (patch) => onChange({ ...value, ...patch });
  const categoryOptions = useMemo(() => distinctOptions(expenses, 'category', categoryLabel), [expenses]);
  const customerOptions = useMemo(() => distinctOptions(expenses, 'customer_name'), [expenses]);
  const paidLabel = PAID_OPTIONS.find((option) => option.value === value.paid)?.label;

  return (
    <FilterToolbar label="Expense filters" onClearAll={() => onChange(EMPTY_EXPENSE_FILTERS)}>
      <FilterField label="Search" icon="bi-search" basis={280}>
        <Input
          type="search"
          value={value.search}
          onChange={(search) => update({ search })}
          placeholder="Description, vendor, customer"
          aria-label="Search expenses"
        />
      </FilterField>

      <FilterField label="Status" basis={170}>
        <SelectPicker
          data={STATUS_OPTIONS}
          value={value.status}
          onChange={(status) => update({ status })}
          placeholder="Any status"
          searchable={false}
          block
        />
      </FilterField>

      <FilterField label="Purchased" basis={250} minWidth={260}>
        <DateRangePicker
          value={value.dates}
          onChange={(dates) => update({ dates })}
          placeholder="Any date"
          format="MM/dd/yyyy"
          character=" – "
          block
        />
      </FilterField>

      <FilterField label="Category" basis={220}>
        <CheckPicker
          data={categoryOptions}
          value={value.categories}
          onChange={(categories) => update({ categories })}
          placeholder="All categories"
          searchable={categoryOptions.length > 8}
          block
        />
      </FilterField>

      <FilterField
        label="Reimbursement"
        chip={value.paid ? { value: paidLabel, onRemove: () => update({ paid: null }) } : null}
      >
        <SelectPicker
          data={PAID_OPTIONS}
          value={value.paid}
          onChange={(paid) => update({ paid })}
          placeholder="Paid or not"
          searchable={false}
          block
        />
      </FilterField>

      <FilterField
        label="Customer"
        chip={value.customer ? { label: 'Customer', value: value.customer, onRemove: () => update({ customer: null }) } : null}
      >
        <SelectPicker
          data={customerOptions}
          value={value.customer}
          onChange={(customer) => update({ customer })}
          placeholder="Any customer"
          block
        />
      </FilterField>
    </FilterToolbar>
  );
}
